import * as React from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  ColourKey,
  EventIndicator,
  MobileCalendar,
  MobileCalendarEvent,
  MobileCalendarHeader,
  selectUser,
  useCacheQuery,
} from 'gossamer-front-end';
import { getFilter, getFirstDateOfMonth, getLastDateOfMonthPlusOne } from 'gossamer-unified';
import { formatEventUrl } from 'shared-front-end';
import {
  BookingComputedStatus,
  HostingStatusOption,
  UserBookingComputedStatusOptions,
  BookingExtendedView,
  EventHostView,
} from 'cb-model';

interface BookingOrEvent {
  eventId: number;
  title: string;
  startAt: string;
  endAt: string;
  bgColor: string;
  textColor: string;
}

export const CalendarMobile = (): React.JSX.Element => {
  const user = useSelector(selectUser);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDate = searchParams.get('date') ? new Date(searchParams.get('date') as string) : new Date();
  const setSelectedDate = (date: Date) => {
    setSearchParams(
      new URLSearchParams({
        date: date.toISOString(),
      }),
    );
  };

  const bookingsQuery = useCacheQuery(
    BookingExtendedView,
    {
      where: {
        and: [
          getFilter('user_id', 'eq', user?.userId || ''),
          getFilter('booking_status', 'eq', 'active'),
          getFilter('end_at', 'gt', getFirstDateOfMonth(selectedDate).toISOString()),
          getFilter('start_at', 'lt', getLastDateOfMonthPlusOne(selectedDate).toISOString()),
        ],
      },
      limit: 1000,
    },
    !user,
  );

  const hostedEventsQuery = useCacheQuery(
    EventHostView,
    {
      where: {
        and: [
          getFilter('user_id', 'eq', user?.userId || ''),
          getFilter('visibility_limit', 'ne', 'cancelled'),
          getFilter('type', 'ne', 'on-demand'),
          getFilter('end_at', 'gt', getFirstDateOfMonth(selectedDate).toISOString()),
          getFilter('start_at', 'lt', getLastDateOfMonthPlusOne(selectedDate).toISOString()),
        ],
      },
      limit: 1000,
    },
    !user,
  );

  const allData: BookingOrEvent[] = (bookingsQuery.data || [])
    .map((booking) => {
      const option = UserBookingComputedStatusOptions.find(
        (item) => item.id === (booking.computed_status.toLowerCase() as BookingComputedStatus),
      );
      return {
        eventId: booking.event_id,
        title: booking.title,
        startAt: booking.start_at,
        endAt: booking.end_at,
        bgColor: option?.bgColor || '#ACBDE9',
        textColor: option?.textColor || '#000000',
      };
    })
    .concat(
      (hostedEventsQuery.data || []).map((hostedEvent) => ({
        eventId: hostedEvent.event_id,
        title: hostedEvent.title,
        startAt: hostedEvent.start_at,
        endAt: hostedEvent.end_at,
        bgColor: HostingStatusOption?.bgColor || '#ACBDE9',
        textColor: HostingStatusOption?.textColor || '#000000',
      })),
    )
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

  const indicators: EventIndicator[] = allData.map((booking) => ({
    date: new Date(booking.startAt),
    colour: booking.bgColor,
  }));
  const eventsToday = allData.filter((booking) => {
    const eventDate = new Date(booking.startAt);
    return (
      eventDate.getFullYear() === selectedDate.getFullYear() &&
      eventDate.getMonth() === selectedDate.getMonth() &&
      eventDate.getDate() === selectedDate.getDate()
    );
  });

  // console.log('NewCalendar: selectedDate', selectedDate, 'indicators', indicators, 'eventsToday', eventsToday);

  const coloursUsed = eventsToday.map((booking) => booking.bgColor);
  return (
    <div>
      <MobileCalendarHeader selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
      <MobileCalendar selectedDate={selectedDate} setSelectedDate={setSelectedDate} indicators={indicators} />
      {eventsToday.map((booking, index) => (
        <div key={index} className="mt-2">
          <MobileCalendarEvent
            title={booking.title}
            startAt={new Date(booking.startAt)}
            endAt={new Date(booking.endAt)}
            colorCode={booking.bgColor || '#ACBDE9'}
            navLink={formatEventUrl(booking.title, booking.eventId)}
          />
        </div>
      ))}
      {eventsToday.length === 0 && <div className="mt-2">No events on {selectedDate.toLocaleDateString()}</div>}
      <div className="mt-3" />
      {eventsToday.length > 0 && (
        <ColourKey items={UserBookingComputedStatusOptions.filter((item) => coloursUsed.includes(item.bgColor))} />
      )}
    </div>
  );
};
